import { useMemo } from 'react';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { BusinessEntitySubscriptions } from './components/BusinessEntitySubscriptions';
import { useAccountSubscriptions } from './hooks/useAccountSubscriptions';

import { useProduct } from '../../hooks/useProduct';
import { useServiceProducts } from '../../hooks/useServiceProducts';

export function Subscriptions() {
  const { formatMessage } = useIntl();
  const { accountSubscriptions, loading, hasError, refetch } = useAccountSubscriptions();

  // Collect all unique service names across all entities for batched queries
  const allServiceNames = useMemo(() => {
    if (!accountSubscriptions?.businessEntities) {
      return [];
    }
    const services = new Set();
    accountSubscriptions.businessEntities.forEach((entity) => {
      entity.subscriptions?.forEach((subscription) => {
        if (subscription.service) {
          services.add(subscription.service);
        }
        subscription.includes?.forEach((included) => {
          if (included.service) {
            services.add(included.service);
          }
        });
      });
    });
    return Array.from(services);
  }, [accountSubscriptions]);

  const { serviceProducts: allServiceProducts, loading: loadingAllServices } = useServiceProducts({
    serviceProductList: allServiceNames,
  });

  const { product: allProducts, loading: loadingAllProducts } = useProduct({
    affiliate: 'pf',
    services: allServiceNames,
  });

  const serviceProductsMap = useMemo(() => {
    if (!allServiceProducts) {
      return null;
    }
    return allServiceProducts.reduce((acc, product) => {
      acc[product.service] = product;
      return acc;
    }, {});
  }, [allServiceProducts]);

  const productsMap = useMemo(() => {
    if (!allProducts || !Array.isArray(allProducts)) {
      return null;
    }
    return allProducts.reduce((acc, product) => {
      if (!acc[product.service]) {
        acc[product.service] = [];
      }
      acc[product.service].push(product);
      return acc;
    }, {});
  }, [allProducts]);

  if (hasError) {
    return (
      <Box>
        <ErrorState
          title={formatMessage({ id: 'dashboard.subscriptions.error.title' })}
          message={formatMessage({ id: 'dashboard.subscriptions.error.message' })}
        />
      </Box>
    );
  }

  if (loading) {
    return <Skeleton variant="rounded" height={300} />;
  }

  return (
    <Box>
      <Stack gap={6}>
        {accountSubscriptions?.businessEntities?.map((entity) => (
          <Stack key={entity.uuid} gap={3}>
            <Text size="xl" weight="bold" family="secondary">
              {entity.name}
            </Text>
            <BusinessEntitySubscriptions
              businessEntity={entity}
              serviceProductsMap={serviceProductsMap}
              productsMap={productsMap}
              loadingAllServices={loadingAllServices}
              loadingAllProducts={loadingAllProducts}
              refetchSubscriptions={refetch}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
